import { useEffect, useRef, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Include cobranza (collections) outcomes so payment_promise / callback_scheduled
// leads also raise a "Leads recientes" notification, not just sales outcomes.
const POSITIVE_OUTCOMES = 'test_drive_scheduled,appointment_scheduled,appointment_booked,interested,payment_promise,callback_scheduled';
const POLL_INTERVAL = 10000;
const STORAGE_KEY = 'lead_sound_enabled';
// Base key; the persisted history is tenant-scoped via `${HISTORY_KEY_BASE}:${tenantId}`
// so one tenant's cached leads never leak into another tenant's bell/list.
const HISTORY_KEY_BASE = 'lead_notif_history';
const HISTORY_CAP = 50;

// Build the tenant-scoped localStorage key. Returns null when the tenant isn't
// loaded yet so callers can no-op gracefully instead of touching a global key.
function historyKeyFor(tenantId) {
  if (!tenantId) return null;
  return `${HISTORY_KEY_BASE}:${tenantId}`;
}

// Friendly display labels per outcome type
const FRIENDLY_OUTCOME = {
  appointment_scheduled: 'agendó cita',
  appointment_booked: 'agendó cita',
  interested: 'interesado',
  test_drive_scheduled: 'agendó prueba de manejo',
  not_interested: 'no interesado',
  payment_promise: 'promesa de pago',
  callback_scheduled: 'llamada agendada',
};

export function getLeadSoundEnabled() {
  // Default ON: absent key → true; only explicit 'false' disables
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  } catch (_) {
    return true;
  }
}

export function setLeadSoundEnabled(val) {
  try {
    localStorage.setItem(STORAGE_KEY, val ? 'true' : 'false');
  } catch (_) {}
}

function readHistory(tenantId) {
  const key = historyKeyFor(tenantId);
  if (!key) return []; // tenant not loaded yet → no cross-tenant global read
  try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (_) { return []; }
}

function writeHistory(items, tenantId) {
  const key = historyKeyFor(tenantId);
  if (!key) return; // tenant not loaded yet → don't persist to a shared key
  try {
    localStorage.setItem(key, JSON.stringify(items.slice(0, HISTORY_CAP)));
  } catch (e) {
    console.warn('[NOTIF] Failed to persist notification history:', e);
  }
}

function appendHistory(entry, tenantId) {
  // Dedup by id before unshift so the same outcome_id can't pile up duplicate
  // entries across reloads/StrictMode double-mounts (in-memory seenIds resets but
  // persisted history doesn't). Newest wins. Idempotent.
  const h = readHistory(tenantId).filter((n) => !(n && entry && n.id != null && n.id === entry.id));
  h.unshift(entry);
  writeHistory(h, tenantId);
}

// Defensively dedupe persisted history by id (newest/first wins). Cleans up
// already-polluted localStorage for existing users on read.
function dedupeById(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.filter((n) => {
    if (!n || n.id == null) return true;
    if (seen.has(n.id)) return false;
    seen.add(n.id);
    return true;
  });
}

// One-time heal for LEGACY cached notifications. Entries cached before the
// call_sid/phone persistence landed lack BOTH campaign_id AND call_sid, so the
// bell-click handler in Layout.js has nothing to resolve a target from — they
// dead-click forever. Drop only those genuinely un-resolvable entries; keep any
// that still carry a campaign_id (direct nav) or a call_sid/phone (backfill path),
// so this never breaks a clickable notification. Idempotent + fail-soft.
function migrateHistory(items, tenantId) {
  try {
    if (!Array.isArray(items) || items.length === 0) return items || [];
    const healed = items.filter((n) => {
      if (!n) return false;
      const resolvable = n.campaign_id || n.call_sid || n.phone;
      return Boolean(resolvable);
    });
    // Persist the pruned history once so the dead entries don't linger across reloads.
    if (healed.length !== items.length) {
      writeHistory(healed, tenantId);
      console.log(`[NOTIF] migrateHistory pruned ${items.length - healed.length} un-resolvable legacy notification(s)`);
    }
    return healed;
  } catch (e) {
    console.warn('[NOTIF] migrateHistory error (non-fatal):', e);
    return items || [];
  }
}

// Initial notifications state: read cached (tenant-scoped) history, then heal
// legacy dead entries. Returns [] until the tenant is known.
function readHealedHistory(tenantId) {
  return dedupeById(migrateHistory(readHistory(tenantId), tenantId));
}

/**
 * Build a friendly notification title following the 4-case fallback:
 *   '{campaign} — {name}: {friendly}' / '{name}: {friendly}' /
 *   '{campaign}: {friendly}' / 'Lead: {friendly}'
 */
function buildNotifText(item) {
  try {
    const friendly = FRIENDLY_OUTCOME[item.outcome] || item.outcome || 'resultado desconocido';
    const name = (item.customer_name || '').trim();
    const campaign = (item.campaign_name || '').trim();
    const apptTime = (item.appointment_time || item.appointment_date || '').trim();
    const isAppt = item.outcome === 'appointment_scheduled' || item.outcome === 'appointment_booked';
    const suffix = isAppt && apptTime ? ` para ${apptTime}` : '';

    if (campaign && name) return `${campaign} — ${name.toUpperCase()}: ${friendly}${suffix}`;
    if (name)             return `${name.toUpperCase()}: ${friendly}${suffix}`;
    if (campaign)         return `${campaign}: ${friendly}${suffix}`;
    return `Lead: ${friendly}${suffix}`;
  } catch (e) {
    console.warn('[NOTIF] buildNotifText error:', e);
    return 'Nuevo lead';
  }
}

export function useLeadNotifications() {
  // Tenant scoping: read the current user's tenant so history is never shared
  // across tenants. useAuth is safe here — this hook only renders inside the
  // authenticated app tree (Layout) which is wrapped by AuthProvider.
  const { user } = useAuth();
  const tenantId = user?.tenant_id || null;
  const tenantIdRef = useRef(tenantId);
  tenantIdRef.current = tenantId;

  const seenIds = useRef(new Set());
  const lastTs = useRef(new Date(Date.now() - 15 * 60 * 1000).toISOString());
  const enabledRef = useRef(getLeadSoundEnabled());
  const [notifications, setNotifications] = useState(() => readHealedHistory(tenantId));
  const [unreadCount, setUnreadCount] = useState(0);

  // When the tenant becomes available (or changes), reload the tenant-scoped
  // history and reset dedupe state so we never surface the previous tenant's
  // cached leads.
  useEffect(() => {
    seenIds.current = new Set();
    setNotifications(readHealedHistory(tenantId));
  }, [tenantId]);

  const clearUnread = useCallback(() => setUnreadCount(0), []);

  // Dismiss a single notification by id
  const dismissNotification = useCallback((id) => {
    if (!id) return;
    try {
      const updated = readHistory(tenantIdRef.current).filter((n) => n.id !== id);
      writeHistory(updated, tenantIdRef.current);
      setNotifications(updated);
    } catch (e) {
      console.warn('[NOTIF] dismissNotification error:', e);
    }
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    try {
      writeHistory([], tenantIdRef.current);
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) {
      console.warn('[NOTIF] clearAllNotifications error:', e);
    }
  }, []);

  // Keep enabledRef in sync so polling closure always reads latest value
  const syncEnabled = useCallback(() => {
    enabledRef.current = getLeadSoundEnabled();
  }, []);

  useEffect(() => {
    window.addEventListener('lead-sound-toggle', syncEnabled);
    return () => window.removeEventListener('lead-sound-toggle', syncEnabled);
  }, [syncEnabled]);

  useEffect(() => {
    let timer;

    const poll = async () => {
      // Don't poll/persist until the tenant is known — avoids writing to an
      // unscoped key and prevents cross-tenant leakage. Reschedule and retry.
      if (!tenantIdRef.current) {
        timer = setTimeout(poll, POLL_INTERVAL);
        return;
      }
      try {
        const { data } = await api.get('/analytics/call-outcomes', {
          params: { since: lastTs.current, outcomes: POSITIVE_OUTCOMES },
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        const newItems = [];
        items.forEach((item) => {
          if (!item) return; // defensive: skip null/undefined entries
          // Dedupe key: call_sid + outcome to collapse multi-path writes per call
          // while preserving truly distinct calls and distinct outcomes per call
          const callSid = item.call_sid || '';
          const outcome = item.outcome || '';
          const dedupeKey = callSid
            ? `${callSid}__${outcome}`
            : (item.outcome_id || null);

          if (dedupeKey && !seenIds.current.has(dedupeKey)) {
            seenIds.current.add(dedupeKey);
            newItems.push(item);
            if (item.created_at && item.created_at > lastTs.current) {
              lastTs.current = item.created_at;
            }
          }
        });

        if (newItems.length > 0) {
          // Always record history
          newItems.forEach((item) => {
            try {
              const text = buildNotifText(item);
              // Stable id: prefer outcome_id, fallback to dedupeKey
              const callSid = item.call_sid || '';
              const outcome = item.outcome || '';
              const notifId = item.outcome_id || (callSid ? `${callSid}__${outcome}` : `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
              appendHistory({
                id: notifId,
                text,
                outcome: item.outcome,
                campaign_name: item.campaign_name || null,
                campaign_id: item.campaign_id || null,
                contact_id: item.contact_id || null,
                // Persist call_sid + phone so the bell-click handler in Layout.js can
                // backfill campaign_id/contact_id (via /calls/{sid}/detail) and fall back
                // to a phone match when the live payload arrives empty. Dropping these
                // here is what silently killed the deep-link backfill path.
                call_sid: callSid || null,
                phone: item.phone || item.to_number || null,
                created_at: item.created_at,
                ts: Date.now(),
              }, tenantIdRef.current);
            } catch (e) {
              console.warn('[NOTIF] Error appending history entry:', e);
            }
          });
          setNotifications(readHistory(tenantIdRef.current));
          setUnreadCount((c) => c + newItems.length);

          // Toast: ALWAYS show — decoupled from sound toggle
          let toasted = false;
          newItems.forEach((item) => {
            if (!item) return;
            try {
              const isAppt = item.outcome === 'appointment_scheduled' || item.outcome === 'appointment_booked';
              if (isAppt) {
                const name = (item.customer_name || '').trim();
                const car = (item.car_interest || '').trim();
                const time = (item.appointment_time || '').trim();
                const parts = [name, car, time].filter(Boolean);
                const detail = parts.length > 0 ? parts.join(' — ') : 'nueva cita agendada';
                console.log('notification_received', item.outcome, item.outcome_id || item.call_sid);
                toast.success(`📅 Nueva cita: ${detail}`, { duration: 6000 });
                toasted = true;
              }
            } catch (e) {
              console.warn('[NOTIF] Toast error:', e);
            }
          });
          if (!toasted) {
            toast.success('🔔 Nuevo lead positivo registrado', { duration: 4000 });
          }

          // Audio: gated behind user opt-in toggle
          if (enabledRef.current) {
            new Audio('/ding.mp3').play().catch(() => {
              // Web Audio API beep fallback for autoplay-blocked browsers
              try {
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.frequency.value = 880;
                gain.gain.setValueAtTime(0.3, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
                osc.start(ctx.currentTime);
                osc.stop(ctx.currentTime + 0.4);
              } catch (_) {}
            });
          }
        }
      } catch (err) {
        // Log network/parse errors at debug level; don't surface to user
        console.debug('[NOTIF] poll error (non-fatal):', err?.message || err);
      } finally {
        timer = setTimeout(poll, POLL_INTERVAL);
      }
    };

    timer = setTimeout(poll, POLL_INTERVAL);
    return () => clearTimeout(timer);
  }, []);

  return { notifications, unreadCount, clearUnread, dismissNotification, clearAllNotifications };
}
